import { EditorModes } from "client/Api/Documents/common";
import { FileViewModel, ListLocationModel } from "client/Api/Documents/SharePoint/common";
import { useCallback } from "react";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import { openInline } from "client_legacy/ndaw/fileDownloader";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	calculateForFileName as calculateSharePointOpenPossibilitiesForFileName
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { tryParseServerErrors } from "client/Validation/serverErrors";
import { ModelPath } from "client/Validation/path";
import { InvalidInputValidationResult } from "client/Validation/input";
import { deleteDocuments, DeleteDocumentsResponse } from "client/Api/Documents/SharePoint/sharePointAction";
import { saveAttachment, SaveAttachmentToSharePointResponseModel } from "client/Api/Email/sharePointAttachments";
import { AttachmentModel } from "client/Api/Email/email";
import {
	MailMessageLocationModel,
	MailMessageLocationQueryModel
} from "client/Api/Email/common";
import { convertToMailMessageLocationQueryModel } from "client/Features/Email/common";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import { environmentHttpClient } from "client/Api/httpClient";
import { openExternalUrlConfirmationDialog } from "client/Features/Documents/ExternalUrlConfirmationDialog/ExternalUrlConfirmationDialog";
import { tryParseUrlFile } from "client/Features/Documents/openUrlFile";

type ViewAttachmentHandlerOptions = {
	attachment: AttachmentModel;
	messageLocation: MailMessageLocationModel;
	onIsBusyChange(isBusy: boolean): void;
};

export function useViewAttachmentHandler(options: ViewAttachmentHandlerOptions): (() => void) {
	const {
		attachment: {
			name,
			exchangeAttachmentId
		},
		messageLocation,
		onIsBusyChange
	} = options;

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const viewAttachmentInWebApp = useCallback(
		async (): Promise<void> => {
			const folderName: string = "tmp";

			onIsBusyChange(true);
			try {
				const savedFileModel: SaveAttachmentToSharePointResponseModel = await saveAttachment(
					convertToMailMessageLocationQueryModel(messageLocation),
					exchangeAttachmentId,
					folderName,
					globalCancellation
				);
				onIsBusyChange(false);
				const file: FileViewModel = savedFileModel.file;

				const editorMode: number = EditorModes.View;
				const shouldHideRibbon: boolean = true;

				openFileInWopi(
					file,
					() => deleteTemporaryDocument(file),
					//When an attachment is opened via e-mail you want to hide the file ribbon
					//because you want the attachment to be in read-only mode
					shouldHideRibbon,
					editorMode
				);
			} catch (e) {

				const result: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(e);

				if (result.wasSuccessful
					&& result.result.has(ModelPath.root)
				) {
					alertManager.showError(
						result.result.get(ModelPath.root)!.errorMessage
					);

				} else {
					alertManager.showError(localize("_AttachmentSaveFailed"));
					console.error(e);
				}

				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			exchangeAttachmentId,
			messageLocation,
			onIsBusyChange,
			globalCancellation
		]
	);

	const viewAttachmentInline = useCallback(
		() => {
			const viewUrl: string = getViewAttachmentUrl(messageLocation, exchangeAttachmentId);

			alertManager.showInfo(localize("_CannotOpenThisFileYet"));
			openInline(viewUrl);
		},
		[
			alertManager,
			exchangeAttachmentId,
			messageLocation
		]
	);

	const openUrlFileHandler = useCallback(
		async (): Promise<void> => {
			try {
				onIsBusyChange(true);

				const fileContent: string = await environmentHttpClient.getText(
					getViewAttachmentUrlPath(messageLocation, exchangeAttachmentId)
				);

				onIsBusyChange(false);

				const fileContentResult: TryResult<string> = tryParseUrlFile(fileContent);

				if (!fileContentResult.wasSuccessful) {
					alertManager.showError(
						localize("_GeneralErrorMessage")
					);
					return;
				}

				openExternalUrlConfirmationDialog(fileContentResult.result);
			} catch (e) {
				console.error(e);
				onIsBusyChange(false);
				alertManager.showError(
					localize("_GeneralErrorMessage")
				);

				return;
			}
		},
		[
			alertManager,
			exchangeAttachmentId,
			messageLocation,
			onIsBusyChange
		]
	);

	return useCallback(
		(): void => {
			const {
				canBeOpenedOnline,
				canBeOpenedAsExternalUrl
			} = calculateSharePointOpenPossibilitiesForFileName(name);

			if (canBeOpenedAsExternalUrl) {
				openUrlFileHandler();
				return;
			}

			if (canBeOpenedOnline) {
				viewAttachmentInWebApp();
				return;
			}

			viewAttachmentInline();
		},
		[
			name,
			viewAttachmentInWebApp,
			viewAttachmentInline,
			openUrlFileHandler
		]
	);
}

function getViewAttachmentUrlPath(
	messageLocation: MailMessageLocationModel,
	exchangeAttachmentId: string
): string {
	const location: MailMessageLocationQueryModel = convertToMailMessageLocationQueryModel(messageLocation);

	const params: string = urlEncodeObject(
		{
			smtpAddress: location.smtpAddress,
			messageId: location.messageId,
			attachmentId: exchangeAttachmentId
		},
		true
	);

	return `Email/ViewExchangeAttachment?${params}`;
}

function getViewAttachmentUrl(
	messageLocation: MailMessageLocationModel,
	exchangeAttachmentId: string
): string {
	return createUrlRelativeToEnvironment(
		getViewAttachmentUrlPath(messageLocation, exchangeAttachmentId)
	);
}

function deleteTemporaryDocument(file: FileViewModel): Promise<DeleteDocumentsResponse> {
	const location: ListLocationModel = convertToLocationModel(file.location);

	return deleteDocuments(location, [file.id]);
}